import { css } from "@emotion/react";
import { FormState } from "final-form";
import { mapValues } from "lodash";
import { FieldInputProps, FormSpy } from "react-final-form";
import { useSelector } from "react-redux";

import { FormField, SpacingToken, Textarea } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { config } from "config/config";
import { selectAgents } from "store/entities/agents";
import { selectLanguageLabels } from "store/entities/application-settings/selectors";
import { selectLicenseId } from "store/entities/license";
import { selectMailboxes } from "store/entities/mailboxes/selectors";
import { selectTags } from "store/entities/tags";
import { selectTeams } from "store/entities/teams";
import { withVar } from "themes/helpers";

import { guessDescription } from "./input";
import { IRuleForm } from "./types";

type IProps = {
  input: FieldInputProps<string>;
};

export const DescriptionInput: React.FC<IProps> = ({ input }) => {
  const agents = useSelector(selectAgents);
  const teams = useSelector(selectTeams);
  const tags = useSelector(selectTags);
  const mailboxesMapRaw = useSelector(selectMailboxes);
  const licenseID = useSelector(selectLicenseId);
  const languageLabels = useSelector(selectLanguageLabels);
  const domainSuffix = config.defaultMailboxSuffix;
  const mailboxesMap = {
    ...mapValues(mailboxesMapRaw, mailbox => ({
      ...mailbox,
      fullEmail: `${licenseID}-${mailbox.name}@${domainSuffix}`,
    })),
    default: {
      id: "default",
      fullEmail: `${licenseID}@${domainSuffix}`,
    },
  };

  const updateWorkflowDescription = (formData: FormState<IRuleForm>): void => {
    if (formData?.active === "description" || formData?.values?.description !== "" || formData.invalid) {
      return;
    }

    const guessedName = guessDescription(formData?.values, agents, teams, tags, mailboxesMap, languageLabels);
    if (guessedName !== null && !formData?.touched?.description) {
      input.onChange(guessedName);
    }
  };

  return (
    <>
      <Flex column width="100%" maxWidth="640px" marginTop={withVar(SpacingToken.Spacing4)}>
        <FormField labelText="Description">
          <Textarea
            {...input}
            data-testid="workflow-description-textarea"
            placeholder="Enter description..."
            value={input.value}
            onChange={event => input.onChange(event.target.value)}
            // TODO: remove styles when api for Textarea will be extended
            css={css`
              width: 100%;
              max-width: 640px;
              textarea {
                resize: none;
                width: 100%;
                max-width: 640px;
                box-sizing: border-box;
                height: 76px;
              }
            `}
          />
        </FormField>
        <FormSpy
          subscription={{ values: true, errors: true, active: true, touched: true, invalid: true }}
          onChange={updateWorkflowDescription}
        />
      </Flex>
    </>
  );
};
